// src/services/apiClient.ts

import axios from 'axios';
import { getAuthToken } from './authGetter';

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

// 1. Create a new Axios instance with a custom configuration.
const apiClient = axios.create({
  baseURL: BASE_URL,
});

// 2. Organization ID resolution from the backend.
// The DB org ID is different from Clerk's org ID. We resolve it once
// by calling /api/users/sync (ensures user+org exist) then caching the result.
let cachedOrgId: string | null = null;
let orgResolutionPromise: Promise<string | null> | null = null;

async function resolveOrganizationId(token: string): Promise<string | null> {
  if (cachedOrgId) return cachedOrgId;
  if (orgResolutionPromise) return orgResolutionPromise;

  orgResolutionPromise = (async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Call sync to ensure the user and default org exist in the DB
      const syncResponse = await axios.post(
        `${BASE_URL}/api/users/sync`,
        {},
        { headers },
      );

      // syncUser returns organizationId directly
      if (syncResponse.data?.organizationId) {
        cachedOrgId = syncResponse.data.organizationId;
        return cachedOrgId;
      }

      // Fallback: fetch organizations list
      const orgsResponse = await axios.get(
        `${BASE_URL}/api/organizations`,
        { headers },
      );

      if (orgsResponse.data?.length > 0) {
        cachedOrgId = orgsResponse.data[0].id;
        return cachedOrgId;
      }

      return null;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[apiClient] Failed to resolve organization from backend', error);
      }
      return null;
    } finally {
      orgResolutionPromise = null;
    }
  })();

  return orgResolutionPromise;
}

// Allow external code (e.g. sign-out) to clear the cached org
export function clearCachedOrganization() {
  cachedOrgId = null;
  orgResolutionPromise = null;
}

// 3. Request interceptor — attaches JWT and X-Organization-ID.
apiClient.interceptors.request.use(async (config) => {
  const token = await getAuthToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;

    // Resolve the DB organization ID (not Clerk's org ID)
    const orgId = await resolveOrganizationId(token);

    if (orgId) {
      config.headers['X-Organization-ID'] = orgId;
    } else if (import.meta.env.DEV) {
      console.warn('[apiClient] Missing organization context for request', config.url);
    }
  }

  return config;
}, (error) => Promise.reject(error));

// Export the configured Axios instance to be used throughout the application.
export default apiClient;
